import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import api, { postLead } from "@/lib/api";
import { track } from "@/lib/site";
import useSWR from "swr";

const fetcher = (url) => api.get(url).then((r) => r.data);

function Field({ label, required, children, className = "" }) {
  return (
    <div className={className}>
      <label className="flabel">
        {label} {required && <span className="text-gold-dark">*</span>}
      </label>
      {children}
    </div>
  );
}

const SUCCESS_MSG =
  "Thank you for contacting Orvella Overseas. Your enquiry has been received. Our export team will review your requirements and contact you shortly.";

function useProductOptions() {
  const { data } = useSWR("/products", fetcher, { fallbackData: [] });
  return useMemo(() => data.map((p) => p.name), [data]);
}

export function QuoteForm({ type = "quote" }) {
  const [params] = useSearchParams();
  const productOptions = useProductOptions();
  const [form, setForm] = useState({
    name: "", company: "", email: "", country: "", phone: "",
    product: params.get("product") || "", quantity: "", packaging: "",
    destination_country: "", destination_port: "", delivery_date: "", private_label: "", message: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await postLead({ type, ...form }, file);
      track(type === "sample" ? "sample_submit" : "quote_submit", { product: form.product });
      setDone(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Something went wrong. Please try again or contact us on WhatsApp.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="card p-8 text-center" data-testid="quote-success">
        <CheckCircle2 size={44} className="mx-auto text-gold-dark" />
        <h3 className="mt-4 font-serif text-2xl font-semibold text-navy">Enquiry Received</h3>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-mist">{SUCCESS_MSG}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card p-6 sm:p-8" data-testid="quote-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Full Name" required>
          <input className="field" required value={form.name} onChange={set("name")} data-testid="quote-name-input" autoComplete="name" />
        </Field>
        <Field label="Company Name" required>
          <input className="field" required value={form.company} onChange={set("company")} data-testid="quote-company-input" autoComplete="organization" />
        </Field>
        <Field label="Business Email" required>
          <input className="field" type="email" required value={form.email} onChange={set("email")} data-testid="quote-email-input" autoComplete="email" />
        </Field>
        <Field label="Country" required>
          <input className="field" required value={form.country} onChange={set("country")} data-testid="quote-country-input" autoComplete="country-name" />
        </Field>
        <Field label="Phone / WhatsApp">
          <input className="field" value={form.phone} onChange={set("phone")} data-testid="quote-phone-input" autoComplete="tel" />
        </Field>
        <Field label="Product" required>
          <input className="field" required list="product-options" value={form.product} onChange={set("product")} data-testid="quote-product-input" placeholder="e.g. 1121 Basmati Rice" />
          <datalist id="product-options">
            {productOptions.map((n) => <option key={n} value={n} />)}
          </datalist>
        </Field>
        <Field label="Required Quantity" required>
          <input className="field" required value={form.quantity} onChange={set("quantity")} data-testid="quote-quantity-input" placeholder="e.g. 1 x 20ft container / 25 MT" />
        </Field>
        <Field label="Preferred Packaging">
          <input className="field" value={form.packaging} onChange={set("packaging")} data-testid="quote-packaging-input" placeholder="e.g. 50 kg bulk bags" />
        </Field>
        <Field label="Destination Country">
          <input className="field" value={form.destination_country} onChange={set("destination_country")} data-testid="quote-dest-country-input" />
        </Field>
        <Field label="Destination Port">
          <input className="field" value={form.destination_port} onChange={set("destination_port")} data-testid="quote-dest-port-input" />
        </Field>
        <Field label="Expected Delivery Date">
          <input className="field" type="date" value={form.delivery_date} onChange={set("delivery_date")} data-testid="quote-delivery-input" />
        </Field>
        <Field label="Private Label Required?">
          <select className="field" value={form.private_label} onChange={set("private_label")} data-testid="quote-private-label-select">
            <option value="">Select…</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
            <option value="To discuss">To discuss</option>
          </select>
        </Field>
        <Field label="Message" className="sm:col-span-2">
          <textarea className="field min-h-24" rows={4} value={form.message} onChange={set("message")} data-testid="quote-message-input" placeholder="Share specifications, packaging details or any other requirement" />
        </Field>
        <Field label="Upload Specification / Requirement (PDF or image)" className="sm:col-span-2">
          <input
            className="field file:mr-3 file:rounded-md file:border-0 file:bg-sand file:px-3 file:py-1.5 file:text-xs file:font-bold file:text-navy"
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="quote-file-input"
          />
        </Field>
      </div>
      {error && <p className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="quote-form-error">{error}</p>}
      <button type="submit" disabled={busy} data-testid="quote-submit-button" className="btn-gold mt-6 w-full disabled:opacity-60 sm:w-auto">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        {busy ? "Submitting…" : type === "sample" ? "Submit Sample Request" : "Submit Quote Request"}
      </button>
    </form>
  );
}

export function ContactForm() {
  const productOptions = useProductOptions();
  const [form, setForm] = useState({ name: "", company: "", country: "", email: "", phone: "", product: "", quantity: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await postLead({ type: "contact", ...form });
      track("contact_submit");
      setDone(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="card p-8 text-center" data-testid="contact-success">
        <CheckCircle2 size={44} className="mx-auto text-gold-dark" />
        <h3 className="mt-4 font-serif text-2xl font-semibold text-navy">Enquiry Received</h3>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-mist">{SUCCESS_MSG}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card p-6 sm:p-8" data-testid="contact-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name" required>
          <input className="field" required value={form.name} onChange={set("name")} data-testid="contact-name-input" />
        </Field>
        <Field label="Company">
          <input className="field" value={form.company} onChange={set("company")} data-testid="contact-company-input" />
        </Field>
        <Field label="Country">
          <input className="field" value={form.country} onChange={set("country")} data-testid="contact-country-input" />
        </Field>
        <Field label="Email" required>
          <input className="field" type="email" required value={form.email} onChange={set("email")} data-testid="contact-email-input" />
        </Field>
        <Field label="Phone">
          <input className="field" value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" />
        </Field>
        <Field label="Product Interested In">
          <input className="field" list="contact-product-options" value={form.product} onChange={set("product")} data-testid="contact-product-input" />
          <datalist id="contact-product-options">
            {productOptions.map((n) => <option key={n} value={n} />)}
          </datalist>
        </Field>
        <Field label="Quantity" className="sm:col-span-2">
          <input className="field" value={form.quantity} onChange={set("quantity")} data-testid="contact-quantity-input" placeholder="e.g. 500 kg / month" />
        </Field>
        <Field label="Message" required className="sm:col-span-2">
          <textarea className="field min-h-24" rows={4} required value={form.message} onChange={set("message")} data-testid="contact-message-input" />
        </Field>
      </div>
      {error && <p className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="contact-form-error">{error}</p>}
      <button type="submit" disabled={busy} data-testid="contact-submit-button" className="btn-gold mt-6 w-full disabled:opacity-60 sm:w-auto">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        {busy ? "Sending…" : "Send Enquiry"}
      </button>
    </form>
  );
}
